import React, { useEffect, useState } from 'react'
import {
  SAVED_REPORTS,
  SavedReport,
  REPORT_DIMENSIONS,
  REPORT_METRICS,
  REPORT_CHARTS,
  DEALS,
  Deal,
  STAGE_MAP,
  gbp,
} from './crmData'
import { Card, Pill, CrmToaster, crmToast } from './crmUi'
import { Button } from '../shared/Button'
import { useCrm } from './useCrm'
import { useAgents } from './useAgents'
import { fetchAllDeals, getSettings, putSettings, getReportSchema, runReport as apiRunReport, type ReportResult } from './crmApi'

interface BuilderState {
  dimension: string
  metric: string
  chart: string
}

const BAR_COLOR = '#2563EB'

/** Group key resolver for a deal under a chosen dimension. `agentName` resolves a real agent id. */
function dimKey(d: Deal, dimension: string, agentName: (id?: string | null) => string): string {
  switch (dimension) {
    case 'stage':
      return STAGE_MAP[d.stage]?.label || d.stage
    case 'owner':
      return agentName(d.ownerId) || d.ownerId
    case 'country':
      return d.country
    case 'source':
    default:
      return d.source
  }
}

interface ResultRow {
  label: string
  value: number
}

function aggregate(deals: Deal[], dimension: string, metric: string, agentName: (id?: string | null) => string): ResultRow[] {
  const groups = new Map<string, Deal[]>()
  for (const d of deals) {
    const k = dimKey(d, dimension, agentName)
    const arr = groups.get(k)
    if (arr) arr.push(d)
    else groups.set(k, [d])
  }

  const rows: ResultRow[] = []
  for (const [label, deals] of groups) {
    let value = 0
    switch (metric) {
      case 'deal_count':
        value = deals.length
        break
      case 'deal_value':
        value = deals.reduce((s, d) => s + d.amount, 0)
        break
      case 'mrr':
        value = deals
          .filter((d) => d.stage === 'successfully_signed_up')
          .reduce((s, d) => s + (d.mrr || 0), 0)
        break
      case 'conv_rate': {
        const signed = deals.filter((d) => d.stage === 'successfully_signed_up').length
        value = deals.length ? Math.round((signed / deals.length) * 100) : 0
        break
      }
      case 'avg_age':
        value = deals.length
          ? Math.round(deals.reduce((s, d) => s + d.createdDays, 0) / deals.length)
          : 0
        break
    }
    rows.push({ label, value })
  }
  return rows.sort((a, b) => b.value - a.value)
}

function formatValue(value: number, metric: string): string {
  if (metric === 'deal_value' || metric === 'mrr') return gbp(value)
  if (metric === 'conv_rate') return `${value}%`
  return String(value)
}

const ToggleRow: React.FC<{
  label: string
  options: { id: string; label: string }[]
  value: string
  onChange: (id: string) => void
}> = ({ label, options, value, onChange }) => (
  <div className="flex items-start gap-3 flex-wrap">
    <span className="w-24 pt-1.5 text-sm font-medium text-txt-secondary shrink-0">{label}</span>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            value === o.id ? 'bg-brand-600 text-white' : 'bg-surface-tertiary text-txt-secondary'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  </div>
)

/**
 * Phase 2.8 — the builder now asks the SERVER, because the questions worth asking need data
 * the browser does not have: platform usage, churn risk, support tickets, module adoption,
 * which show a lead came from. It could previously only group four fields off the deal row.
 *
 * And every row carries its SAMPLE SIZE. A group of 40 leads where 3 have usage data does not
 * have an "average activity of 128" — it has an average across three leads and 37 unknowns.
 * Presenting the first as the second is how a dashboard becomes confidently wrong.
 */
export const CrmReportBuilder: React.FC = () => {
  const { agentName } = useAgents()
  const [builder, setBuilder] = useState<BuilderState>({
    dimension: 'source',
    metric: 'deal_count',
    chart: 'bar',
  })
  const [ran, setRan] = useState(false)
  const [reports, setReports] = useState<SavedReport[]>(SAVED_REPORTS.map((r) => ({ ...r })))

  const { data: deals } = useCrm(
    () => fetchAllDeals({ includeTerminal: true }) as Promise<Deal[]>,
    DEALS
  )

  // Server-computed (2.8): the metrics below need usage / churn / support / adoption, none of
  // which the browser has. Falls back to the old client aggregation if the API is unreachable.
  const [schema, setSchema] = useState<{ dimensions: { key: string; label: string }[]; metrics: { key: string; label: string; needsEvidence: boolean }[] } | null>(null)
  const [result, setResult] = useState<ReportResult | null>(null)
  const [running, setRunning] = useState(false)

  useEffect(() => { getReportSchema().then(setSchema).catch(() => setSchema(null)) }, [])

  useEffect(() => {
    let cancelled = false
    setRunning(true)
    apiRunReport(builder.dimension, builder.metric)
      .then((r) => { if (!cancelled) setResult(r) })
      .catch(() => { if (!cancelled) setResult(null) })
      .finally(() => { if (!cancelled) setRunning(false) })
    return () => { cancelled = true }
  }, [builder.dimension, builder.metric])

  const rows: { label: string; value: number; n?: number; of?: number; provisional?: boolean }[] =
    result ? result.rows : aggregate(deals, builder.dimension, builder.metric, agentName)
  const maxVal = rows.reduce((m, r) => Math.max(m, r.value), 0) || 1

  // Saved reports persist server-side (GET/PUT /crm/settings → saved_reports).
  useEffect(() => {
    getSettings()
      .then((s: any) => {
        if (Array.isArray(s?.savedReports) && s.savedReports.length) setReports(s.savedReports)
      })
      .catch(() => {})
  }, [])

  const persist = (next: SavedReport[]) => {
    setReports(next)
    putSettings({ savedReports: next }).catch(() => crmToast('Saved locally — could not reach the server', 'error'))
  }

  const loadReport = (r: SavedReport) => {
    setBuilder({
      dimension: r.dimension,
      metric: r.metric,
      chart: r.chart === 'funnel' ? 'bar' : r.chart,
    })
    setRan(true)
    crmToast(`Loaded "${r.name}"`)
  }

  const saveReport = () => {
    const suggested = `${builder.metric.replace(/_/g, ' ')} by ${builder.dimension}`
    const name = window.prompt('Save report as:', suggested)
    if (!name || !name.trim()) return
    const next: SavedReport = {
      id: `rep_${Date.now().toString(36)}`,
      name: name.trim(),
      dimension: builder.dimension,
      metric: builder.metric,
      chart: builder.chart,
    }
    persist([...reports, next])
    crmToast('Report saved', 'success')
  }

  const deleteReport = (id: string) => {
    persist(reports.filter((r) => r.id !== id))
    crmToast('Report deleted')
  }

  const exportCsv = () => {
    if (!rows.length) {
      crmToast('Nothing to export — run a report first', 'error')
      return
    }
    const esc = (v: unknown) => `"${String(v).replace(/"/g, '""')}"`
    const csv = [
      // The export carries the sample size too — a CSV that drops it is a CSV that lies later.
      [esc(builder.dimension), esc(builder.metric), esc('computed_from'), esc('group_size'), esc('confidence')].join(','),
      ...rows.map((r) => [esc(r.label), esc(r.value), esc(r.n ?? ''), esc(r.of ?? ''), esc(r.provisional ? 'provisional' : '')].join(',')),
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${builder.metric}-by-${builder.dimension}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    crmToast('CSV exported', 'success')
  }

  return (
    <div className="h-full flex flex-col bg-surface-secondary">
      <CrmToaster />
      <header className="px-6 pt-6 pb-4 bg-surface border-b border-border">
        <h1 className="text-xl font-bold text-txt-primary">Report builder</h1>
      </header>

      <div className="flex-1 min-h-0 overflow-auto p-6 space-y-4">
        <Card title="Build a report">
          <div className="space-y-3">
            <ToggleRow
              label="Dimension"
              options={schema ? schema.dimensions.map((d) => ({ id: d.key, label: d.label })) : REPORT_DIMENSIONS}
              value={builder.dimension}
              onChange={(id) => setBuilder((b) => ({ ...b, dimension: id }))}
            />
            <ToggleRow
              label="Metric"
              options={schema ? schema.metrics.map((m) => ({ id: m.key, label: m.label })) : REPORT_METRICS}
              value={builder.metric}
              onChange={(id) => setBuilder((b) => ({ ...b, metric: id }))}
            />
            <ToggleRow
              label="Chart type"
              options={REPORT_CHARTS}
              value={builder.chart}
              onChange={(id) => setBuilder((b) => ({ ...b, chart: id }))}
            />
            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" onClick={() => setRan(true)}>
                Run report
              </Button>
              <Button size="sm" variant="secondary" onClick={saveReport}>
                Save report
              </Button>
              <Button size="sm" variant="secondary" onClick={exportCsv}>
                ⬇ Export CSV
              </Button>
            </div>
          </div>
        </Card>

        <Card title="Results">
          {/* What this report is standing on. A metric only some leads have data for must say so. */}
          {ran && result && (
            <p
              className="text-[11px] mb-3 px-3 py-2 rounded-lg leading-relaxed"
              style={
                result.coverage.withEvidence < result.coverage.total
                  ? { background: '#FEF3C7', color: '#92400E' }
                  : { background: '#F1F5F9', color: '#475569' }
              }
            >
              {result.coverage.note}
            </p>
          )}
          {running && <p className="text-[11px] text-txt-tertiary mb-2">Running…</p>}

          {!ran ? (
            <p className="text-sm text-txt-tertiary py-6 text-center">
              Choose dimensions and click Run report
            </p>
          ) : builder.chart === 'table' ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-txt-tertiary border-b border-border">
                  <th className="py-2 font-medium">
                    {REPORT_DIMENSIONS.find((d) => d.id === builder.dimension)?.label}
                  </th>
                  <th className="py-2 font-medium text-right">
                    {result ? result.metricLabel : REPORT_METRICS.find((m) => m.id === builder.metric)?.label}
                  </th>
                  <th className="py-2 font-medium text-right w-32">Based on</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.label} className="border-b border-border last:border-0">
                    <td className="py-2 text-txt-primary">{r.label}</td>
                    <td className="py-2 text-right font-semibold text-txt-primary">
                      {formatValue(r.value, builder.metric)}
                      {r.provisional && (
                        <span className="ml-1.5 text-[10px] font-bold px-1 py-0.5 rounded" style={{ background: '#FEF3C7', color: '#92400E' }}>
                          PROVISIONAL
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-right text-[11px] text-txt-tertiary">
                      {r.n == null || r.of == null ? '—' : r.n === r.of ? `${r.of} lead${r.of === 1 ? '' : 's'}` : `${r.n} of ${r.of}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="space-y-2.5">
              {rows.map((r) => (
                <div key={r.label} className="flex items-center gap-3">
                  <span className="w-32 shrink-0 text-xs font-medium text-txt-secondary truncate">
                    {r.label}
                  </span>
                  <div className="flex-1 h-6 rounded-md bg-surface-tertiary overflow-hidden">
                    <div
                      className="h-full rounded-md"
                      style={{
                        width: `${Math.max((r.value / maxVal) * 100, 3)}%`,
                        background: BAR_COLOR,
                      }}
                    />
                  </div>
                  <span className="w-20 shrink-0 text-right text-xs font-semibold text-txt-primary">
                    {formatValue(r.value, builder.metric)}
                  </span>
                  <span className="w-24 shrink-0 text-right text-[10px] text-txt-tertiary">
                    {r.n == null || r.of == null ? '' : r.n === r.of ? `${r.of}` : `${r.n} of ${r.of}`}
                    {r.provisional && <span style={{ color: '#B45309' }}> · provisional</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Saved reports">
          <div className="divide-y divide-border -my-1">
            {reports.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-txt-primary truncate">{r.name}</span>
                  <Pill tone="gray">{r.chart}</Pill>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="secondary" onClick={() => loadReport(r)}>
                    Load
                  </Button>
                  <button
                    onClick={() => deleteReport(r.id)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-txt-tertiary hover:text-red-600 hover:bg-surface-hover"
                    title="Delete report"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  )
}
